import { useRef } from 'react';
import {
  Upload,
  Download,
  Loader2,
  FileText,
  Check,
  X,
  AlertCircle,
  Ban,
  CheckCircle,
} from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '../../../../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../../../../components/ui/alert';
import { CsvParseResult, BulkOperationResult } from '../types';

interface BulkOperationsProps {
  file: File | null;
  parseResult: CsvParseResult | null;
  operationResult: BulkOperationResult | null;
  isParsing: boolean;
  isProcessing: boolean;
  parseError: string | null;
  processError: string | null;
  onFileSelect: (file: File) => void;
  onProcess: (action: 'block' | 'unblock') => void;
  onReset: () => void;
  onDownloadTemplate: () => void;
  // Whether the user may run the bulk block/unblock (entregoCadastroBulkUpdate key).
  canProcess?: boolean;
}

export function BulkOperations({
  file,
  parseResult,
  operationResult,
  isParsing,
  isProcessing,
  parseError,
  processError,
  onFileSelect,
  onProcess,
  onReset,
  onDownloadTemplate,
  canProcess = true,
}: BulkOperationsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileSelect(selectedFile);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const hasValidDocuments = parseResult && parseResult.valid.length > 0;
  const hasInvalidDocuments = parseResult && parseResult.invalid.length > 0;
  const hasOperationResult = operationResult !== null;

  return (
    <Card className="animate-in fade-in-50 duration-300">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Operações em Lote
        </CardTitle>
        <CardDescription>
          Envie um arquivo CSV com lista de CPF/CNPJ para bloquear ou
          desbloquear em massa
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
          <Button variant="outline" onClick={onDownloadTemplate}>
            <Download className="mr-2 h-4 w-4" />
            Baixar Template CSV
          </Button>

          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              variant="secondary"
              onClick={handleButtonClick}
              disabled={isParsing}
              className="w-full sm:w-auto"
            >
              {isParsing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Selecionar Arquivo CSV
                </>
              )}
            </Button>
          </div>
        </div>

        {file && (
          <div className="animate-in slide-in-from-top-2 fade-in-50 duration-200 flex items-center gap-2 rounded-md bg-muted p-3">
            <FileText className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">{file.name}</span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto h-6 w-6"
              onClick={onReset}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}

        {parseError && (
          <Alert variant="destructive" className="animate-in fade-in-50 duration-200">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erro ao processar arquivo</AlertTitle>
            <AlertDescription>{parseError}</AlertDescription>
          </Alert>
        )}

        {parseResult && (
          <div className="animate-in slide-in-from-top-2 fade-in-50 duration-300 space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant="default" className="text-sm">
                <Check className="mr-1 h-3 w-3" />
                {parseResult.valid.length} documento(s) válido(s)
              </Badge>
              {hasInvalidDocuments && (
                <Badge variant="destructive" className="text-sm">
                  <X className="mr-1 h-3 w-3" />
                  {parseResult.invalid.length} documento(s) inválido(s)
                </Badge>
              )}
            </div>

            {hasInvalidDocuments && (
              <Alert variant="destructive" className="animate-in fade-in-50 duration-200">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Documentos inválidos encontrados</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    <ul className="space-y-1 text-sm">
                      {parseResult.invalid.slice(0, 10).map((item, index) => (
                        <li key={index}>
                          Linha {item.line}: {item.value} - {item.reason}
                        </li>
                      ))}
                      {parseResult.invalid.length > 10 && (
                        <li className="font-medium">
                          ... e mais {parseResult.invalid.length - 10} erro(s)
                        </li>
                      )}
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {hasValidDocuments && (
              <Alert className="animate-in fade-in-50 duration-200 border-blue-200 bg-blue-50">
                <FileText className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-700">
                  Documentos prontos para processamento
                </AlertTitle>
                <AlertDescription className="text-blue-600">
                  {parseResult.valid.length} documento(s) serão processados.
                  Escolha a ação desejada abaixo.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {processError && (
          <Alert variant="destructive" className="animate-in fade-in-50 duration-200">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Erro na operação</AlertTitle>
            <AlertDescription>{processError}</AlertDescription>
          </Alert>
        )}

        {hasOperationResult && (
          <div className="animate-in slide-in-from-top-2 fade-in-50 duration-300 space-y-4">
            <Alert className="border-green-200 bg-green-50">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertTitle className="text-green-700">
                Operação concluída
              </AlertTitle>
              <AlertDescription className="text-green-600">
                {operationResult.success.length} documento(s) processado(s) com
                sucesso.
                {operationResult.failed.length > 0 && (
                  <span className="text-red-600">
                    {' '}
                    {operationResult.failed.length} documento(s) falharam.
                  </span>
                )}
              </AlertDescription>
            </Alert>

            {operationResult.failed.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Falhas no processamento</AlertTitle>
                <AlertDescription>
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    <ul className="space-y-1 text-sm">
                      {operationResult.failed.slice(0, 10).map((item, index) => (
                        <li key={index}>
                          {item.documento}: {item.reason}
                        </li>
                      ))}
                      {operationResult.failed.length > 10 && (
                        <li className="font-medium">
                          ... e mais {operationResult.failed.length - 10} erro(s)
                        </li>
                      )}
                    </ul>
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        <div className="rounded-lg border border-dashed p-4">
          <h4 className="mb-2 font-medium text-sm">Formato do CSV esperado:</h4>
          <pre className="rounded bg-muted p-2 text-xs text-muted-foreground">
            {`documento
12345678901      (CPF)
12345678901234   (CNPJ)`}
          </pre>
          <p className="mt-2 text-xs text-muted-foreground">
            O arquivo deve conter uma coluna com os números de CPF (11 dígitos)
            ou CNPJ (14 dígitos). A primeira linha pode conter um cabeçalho
            (documento, cpf ou cnpj).
          </p>
        </div>
      </CardContent>
      <CardFooter className="flex justify-end gap-2 border-t pt-4">
        {hasOperationResult ? (
          <Button variant="outline" onClick={onReset}>
            <Upload className="mr-2 h-4 w-4" />
            Nova Operação
          </Button>
        ) : (
          <>
            <Button
              variant="destructive"
              onClick={() => onProcess('block')}
              disabled={!hasValidDocuments || isProcessing || !canProcess}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <Ban className="mr-2 h-4 w-4" />
                  Bloquear Todos
                </>
              )}
            </Button>
            <Button
              onClick={() => onProcess('unblock')}
              disabled={!hasValidDocuments || isProcessing || !canProcess}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processando...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Desbloquear Todos
                </>
              )}
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}
